import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LoginUser, reset, getMe } from "../features/authSlice";
import { HiUser, HiLockClosed, HiEye, HiEyeOff } from "react-icons/hi";
import logo from "../elkom.png";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isError, isSuccess, isLoading, message } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (!sessionStorage.getItem("reloaded")) {
      sessionStorage.setItem("reloaded", "true");
      window.location.reload(true);
    }
  }, []);

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    if (user || isSuccess) {
      navigate("/dashboard");
    }
    dispatch(reset());
  }, [user, isSuccess, dispatch, navigate]);

  const Auth = (e) => {
    e.preventDefault();
    dispatch(LoginUser({ username, password }));
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)" }}
    >
      {/* Decorative circles */}
      <div className="login-circle-a absolute top-[-80px] left-[-80px] w-80 h-80 rounded-full"
        style={{ background: "radial-gradient(circle, #04befe, transparent)" }} />
      <div className="login-circle-b absolute bottom-[-100px] right-[-60px] w-96 h-96 rounded-full"
        style={{ background: "radial-gradient(circle, #4481eb, transparent)" }} />

      <div className="relative w-full max-w-sm mx-4">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

          {/* Header band */}
          <div className="px-8 pt-8 pb-6 text-center"
            style={{ background: "linear-gradient(135deg, #1a3a4a 0%, #2c5364 100%)" }}
          >
            <div className="flex justify-center mb-3">
              <div className="bg-white rounded-xl p-2 shadow-lg inline-block">
                <img src={logo} alt="EPS Logo" className="h-14 w-auto object-contain" />
              </div>
            </div>
            <h1 className="text-white font-bold text-lg tracking-wide">EPS</h1>
            <p className="text-blue-200 text-xs mt-0.5 tracking-wider">Elkom Integrated Management System</p>
            <p className="text-blue-300 text-xs opacity-70 mt-0.5">PT. Elkom Printama Batam</p>
          </div>

          {/* Form */}
          <div className="px-8 py-7">
            <p className="text-gray-500 text-xs text-center mb-5 uppercase tracking-widest font-semibold">
              Sign in to continue
            </p>

            {isError && (
              <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <span className="text-red-500 text-lg">!</span>
                <p className="text-red-600 text-xs">{message}</p>
              </div>
            )}

            <form onSubmit={Auth} className="flex flex-col gap-4">
              {/* Username */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Username
                </label>
                <div className="relative">
                  <HiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                  <input
                    type="text"
                    placeholder="Enter your username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50 transition"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Password
                </label>
                <div className="relative">
                  <HiLockClosed className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-10 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <HiEyeOff className="text-lg" /> : <HiEye className="text-lg" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="mt-1 w-full py-2.5 rounded-lg text-sm font-bold text-white transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: isLoading ? "#7aa8c4" : "linear-gradient(135deg, #1a3a4a 0%, #2c5364 100%)" }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-blue-300 text-xs mt-4 opacity-60">
          &copy; {new Date().getFullYear()} PT. Elkom Printama Batam
        </p>
      </div>
    </div>
  );
};

export default Login;
